import type {
  CabinUpgradeEligibilityUpsertItem,
  CabinUpgradePricingUpsertItem,
  CombinedUpgradeApiRow,
} from '~/services/CharterService';

import { type FlightUpgradeRow, VALID_UPGRADE_COMBINATIONS, comboKey } from './CharterUpgradePricingTypes';

/** DD/MM/YYYY → YYYY-MM-DD. Passes through if already YYYY-MM-DD or unrecognised. */
export function parseDateInput(input: string): string {
  const trimmed = input.trim();
  const m = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  return trimmed;
}

/** YYYY-MM-DD → DD/MM/YYYY for display. Returns original if unrecognised. */
export function formatDateDisplay(value: string | null | undefined): string {
  if (!value) return '';
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return value;
}

export type UpgradePricingFilters = {
  departureDateFrom?: string;
  departureDateTo?: string;
  depAirport?: string;
  arrAirport?: string;
};

/** Convert absolute CHD/INF price to percentage of ADT price for display. */
export function toPct(priceAdt: number | null, price: number | null): number | null {
  if (priceAdt == null || price == null) return null;
  if (priceAdt === 0) return 100;
  return Math.round((price / priceAdt) * 100);
}

/** Convert percentage back to absolute price for saving. */
export function fromPct(priceAdt: number, pct: number): number {
  return Math.round(priceAdt * (pct / 100) * 100) / 100;
}

/**
 * Pivot flat API rows (9 per flight) into one FlightUpgradeRow per flight.
 */
export function pivotToFlightRows(apiRows: CombinedUpgradeApiRow[]): FlightUpgradeRow[] {
  const byFlight = new Map<string, FlightUpgradeRow>();

  for (const row of apiRows) {
    if (!byFlight.has(row.flightInstanceId)) {
      byFlight.set(row.flightInstanceId, {
        id: row.flightInstanceId,
        depDate: row.depDate,
        direction: `${row.depAirport} → ${row.arrAirport}`,
        eligibilityId: row.eligibilityId,
        daysBeforeDepNonTierBronze: row.daysBeforeDepNonTierBronze,
        daysBeforeDepSilver: row.daysBeforeDepSilver,
        daysBeforeDepGoldPlatinum: row.daysBeforeDepGoldPlatinum,
      });
    }

    const flightRow = byFlight.get(row.flightInstanceId)!;
    const key = comboKey(row.fromCabinClass, row.toCabinClass);

    flightRow[`${key}_pricingId`] = row.pricingId;
    flightRow[`${key}_priceAdt`] = row.priceAdt;
    flightRow[`${key}_chdPct`] = toPct(row.priceAdt, row.priceChd);
    flightRow[`${key}_infPct`] = toPct(row.priceAdt, row.priceInf);
    flightRow[`${key}_currency`] = row.currency;
  }

  return Array.from(byFlight.values());
}

/**
 * Build eligibility upsert payload from edited rows.
 * Only includes flights where day window fields actually changed vs base.
 */
export function buildEligibilityPayload(
  editedEligibility: Map<string, CabinUpgradeEligibilityUpsertItem>,
): CabinUpgradeEligibilityUpsertItem[] {
  return Array.from(editedEligibility.values());
}

/**
 * Build global pricing upsert payload from edited pricing combos.
 * Deduplicates by (from, to) — last edit wins.
 */
export function buildPricingPayload(
  editedPricing: Map<string, CabinUpgradePricingUpsertItem>,
): CabinUpgradePricingUpsertItem[] {
  return Array.from(editedPricing.values());
}

/**
 * Called from processRowUpdate to detect and record eligibility changes.
 */
export function extractEligibilityChange(
  newRow: FlightUpgradeRow,
  oldRow: FlightUpgradeRow,
): CabinUpgradeEligibilityUpsertItem | null {
  if (
    newRow.daysBeforeDepNonTierBronze === oldRow.daysBeforeDepNonTierBronze &&
    newRow.daysBeforeDepSilver === oldRow.daysBeforeDepSilver &&
    newRow.daysBeforeDepGoldPlatinum === oldRow.daysBeforeDepGoldPlatinum
  ) {
    return null;
  }
  return {
    flightInstanceId: newRow.id,
    daysBeforeDepNonTierBronze: newRow.daysBeforeDepNonTierBronze ?? 0,
    daysBeforeDepSilver: newRow.daysBeforeDepSilver ?? 0,
    daysBeforeDepGoldPlatinum: newRow.daysBeforeDepGoldPlatinum ?? 0,
  };
}

/**
 * Called from processRowUpdate to detect and record pricing changes.
 * Returns a map of changed combos (comboKey → upsert item).
 */
export function extractPricingChanges(
  newRow: FlightUpgradeRow,
  oldRow: FlightUpgradeRow,
): Map<string, CabinUpgradePricingUpsertItem> {
  const changes = new Map<string, CabinUpgradePricingUpsertItem>();

  if (!newRow.eligibilityId) return changes;

  for (const combo of VALID_UPGRADE_COMBINATIONS) {
    const key = comboKey(combo.from, combo.to);
    const priceAdtNew = newRow[`${key}_priceAdt`] as number | null;
    const priceAdtOld = oldRow[`${key}_priceAdt`] as number | null;
    const chdPctNew = newRow[`${key}_chdPct`] as number | null;
    const chdPctOld = oldRow[`${key}_chdPct`] as number | null;
    const infPctNew = newRow[`${key}_infPct`] as number | null;
    const infPctOld = oldRow[`${key}_infPct`] as number | null;

    const changed = priceAdtNew !== priceAdtOld || chdPctNew !== chdPctOld || infPctNew !== infPctOld;

    if (changed) {
      if (priceAdtNew != null) {
        // Upsert
        changes.set(key, {
          eligibilityId: newRow.eligibilityId as string,
          fromCabinClass: combo.from,
          toCabinClass: combo.to,
          priceAdt: priceAdtNew,
          priceChd: fromPct(priceAdtNew, chdPctNew ?? 100),
          priceInf: fromPct(priceAdtNew, infPctNew ?? 100),
          currency: (newRow[`${key}_currency`] as string | null) ?? 'AUD',
        });
      } else if (priceAdtOld != null) {
        // Delete — ADT was non-null, now cleared
        changes.set(key, {
          eligibilityId: newRow.eligibilityId as string,
          fromCabinClass: combo.from,
          toCabinClass: combo.to,
          priceAdt: null,
          priceChd: null,
          priceInf: null,
          currency: (newRow[`${key}_currency`] as string | null) ?? 'AUD',
        });
      }
      // Both old and new are null → no pricing record exists, nothing to do
    }
  }

  return changes;
}
